import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getCameras } from 'api/canvms';
import { displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import { VMSCamera } from '../types/canvision.types';

export function useCameras(siteId: number | null) {
  const dispatch = useDispatch();
  const [cameras, setCameras] = useState<VMSCamera[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchCameras = useCallback(async (isRefresh = false) => {
    if (!siteId) {
      setCameras([]);
      initialLoadDone.current = false;
      return;
    }

    try {
      // Only show loading on initial load, not refreshes
      if (!isRefresh && !initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);
      const response = await getCameras(siteId);
      setCameras(response.data?.cameras || []);
      initialLoadDone.current = true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch cameras';
      console.error('[CANVMS/fetchCameras] Error fetching cameras:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [siteId, dispatch]);

  useEffect(() => {
    initialLoadDone.current = false;
    fetchCameras(false);
  }, [fetchCameras]);

  const refetch = useCallback(() => fetchCameras(true), [fetchCameras]);

  return { cameras, loading, error, refetch };
}
